import { useEffect, useMemo, useState } from "react";
import { fetchMovimientosSesion as fetchEmpresaMovimientos } from "@/api/reportes";
import { useUser } from "@/hooks/useUser";

/* ========= Utils ========= */
const money = (v) =>
    `$${Number(v || 0).toLocaleString("es-MX", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

// --- helpers de fecha ---
const asDate = (s) => {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d) ? null : d;
};
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);

const fdate = (s) =>
    s
        ? new Date(s).toLocaleString("es-MX", {
            hour12: false,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        })
        : "—";

const TIPO_LABEL = {
    set_credit_limit: "Set límite crédito",
    deposito: "Depósito",
    extra_promocional: "Crédito promocional",
    extra_temporal: "Crédito temporal",
    deposito_asignacion: "Depósito asignado (cliente)",
    deposito_transferencia: "Transferencia depósito",
    consumo: "Consumo",
};

const NAT_LABEL = { abono: "Abono (+)", cargo: "Cargo (−)" };

function normalize(s) {
    return s.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
}

export default function ComentariosPanel({
    clienteId = null,
    adminEmpresaId = null, // si viene, forzamos modo admin
}) {
    const user = useUser();
    const perfilId = user?.perfil_id ?? null;
    const isAdmin = [1, 5].includes(perfilId) || !!adminEmpresaId;

    // filtros de fecha
    const [dateMode, setDateMode] = useState("range"); // "range" | "month"
    const [dateStart, setDateStart] = useState("");    // "YYYY-MM-DD"
    const [dateEnd, setDateEnd] = useState("");        // "YYYY-MM-DD"
    const [monthSel, setMonthSel] = useState("");      // "YYYY-MM"

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    // búsqueda y paginación
    const [query, setQuery] = useState("");
    const [pageSize, setPageSize] = useState(10); // puede ser número o "all"
    const [page, setPage] = useState(1);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                // 🔐 por sesión o por admin según override
                const data = await fetchEmpresaMovimientos({ clienteId, adminEmpresaId });
                setRows(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error("Error cargando movimientos:", e);
                setRows([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [clienteId, adminEmpresaId]);

    const mapped = useMemo(
        () =>
            rows.map((r) => ({
                id: r.id,
                fecha_registro_pago: r.fecha_registro_pago ?? null,
                fecha_alta_registro: r.fecha_alta_registro ?? null,
                tipo_raw: r.tipo || "—",
                tipo_label: TIPO_LABEL[r.tipo] || r.tipo || "—",
                naturaleza: NAT_LABEL[r.naturaleza] || r.naturaleza || "—",
                monto: Number(r.monto || 0),
                comentario: r.comentario ?? "",
                comentario_interno: r.comentario_interno ?? "",
            })),
        [rows]
    );

    const filtered = useMemo(() => {
        // texto
        const q = normalize(query.trim());
        let base = mapped;
        if (q) {
            base = base.filter((r) => {
                const bag =
                    normalize(r.comentario || "") +
                    " " +
                    normalize(r.comentario_interno || "") +
                    " " +
                    normalize(r.tipo_label || "") +
                    " " +
                    normalize(r.naturaleza || "");
                return bag.includes(q);
            });
        }

        // fechas
        let dStart = null, dEnd = null;
        if (dateMode === "range" && (dateStart || dateEnd)) {
            if (dateStart) dStart = startOfDay(new Date(dateStart));
            if (dateEnd) dEnd = endOfDay(new Date(dateEnd));
        } else if (dateMode === "month" && monthSel) {
            const [y, m] = monthSel.split("-").map(Number); // YYYY, MM
            dStart = new Date(y, m - 1, 1, 0, 0, 0);
            dEnd = new Date(y, m, 0, 23, 59, 59); // último día del mes
        }

        if (dStart || dEnd) {
            base = base.filter((r) => {
                const d = asDate(r.fecha_registro_pago);
                if (!d) return false;
                if (dStart && d < dStart) return false;
                if (dEnd && d > dEnd) return false;
                return true;
            });
        }

        return base;
    }, [mapped, query, dateMode, dateStart, dateEnd, monthSel]);

    // Totales de la vista filtrada
    const totFiltrado = useMemo(() => {
        let abonos = 0, cargos = 0;
        for (const r of filtered) {
            const isAbono = r.naturaleza.includes("(+)");
            if (isAbono) abonos += r.monto;
            else cargos += r.monto;
        }
        return { abonos, cargos, balance: abonos - cargos };
    }, [filtered]);

    // paginación (soporta "All")
    const total = filtered.length;
    const isAll = String(pageSize) === "all";
    const totalPages = isAll ? 1 : Math.max(1, Math.ceil(total / Number(pageSize)));
    const safePage = Math.min(page, totalPages);
    const start = isAll ? 0 : (safePage - 1) * Number(pageSize);
    const paged = isAll ? filtered : filtered.slice(start, start + Number(pageSize));

    // reset página cuando cambian filtros/criterios
    useEffect(() => {
        setPage(1);
    }, [query, pageSize, dateMode, dateStart, dateEnd, monthSel]);

    /* ============= CONTROLS + CHIPS ============= */
    const ControlsBar = (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-3">
            {/* búsqueda */}
            <div className="relative flex-1">
                <input
                    key="mov-search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Buscar por comentario, tipo, naturaleza…"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">
                    {total} registros
                </div>
            </div>

            {/* filtros de fecha */}
            <div className="flex flex-wrap items-center gap-2">
                <select
                    value={dateMode}
                    onChange={(e) => setDateMode(e.target.value)}
                    className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm"
                    title="Modo de filtro"
                >
                    <option value="range">Dias</option>
                    <option value="month">Mes</option>
                </select>

                {dateMode === "range" ? (
                    <>
                        <input
                            type="date"
                            value={dateStart}
                            onChange={(e) => setDateStart(e.target.value)}
                            className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm"
                            title="Desde"
                        />
                        <input
                            type="date"
                            value={dateEnd}
                            onChange={(e) => setDateEnd(e.target.value)}
                            className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm"
                            title="Hasta"
                        />
                    </>
                ) : (
                    <input
                        type="month"
                        value={monthSel}
                        onChange={(e) => setMonthSel(e.target.value)}
                        className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm"
                        title="Mes"
                    />
                )}

                <button
                    onClick={() => { setDateStart(""); setDateEnd(""); setMonthSel(""); }}
                    className="px-2 py-1 text-sm rounded border border-slate-300 hover:bg-slate-50"
                    title="Limpiar fechas"
                >
                    Limpiar
                </button>
            </div>

            {/* filas + paginación */}
            <div className="flex items-center gap-2">
                <label className="text-xs text-slate-600">Filas:</label>
                <select
                    value={String(pageSize)}
                    onChange={(e) =>
                        setPageSize(e.target.value === "all" ? "all" : Number(e.target.value))
                    }
                    className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm"
                >
                    {[5, 10, 20, 50].map((n) => (
                        <option key={n} value={n}>{n}</option>
                    ))}
                    <option value="all">All</option>
                </select>

                <div className="flex items-center gap-1">
                    <button
                        onClick={() => setPage(1)}
                        disabled={safePage === 1 || isAll}
                        className="px-2 py-1 text-sm rounded border border-slate-300 disabled:opacity-40"
                        title="Primera"
                    >
                        «
                    </button>
                    <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={safePage === 1 || isAll}
                        className="px-2 py-1 text-sm rounded border border-slate-300 disabled:opacity-40"
                        title="Anterior"
                    >
                        ‹
                    </button>
                    <span className="px-2 text-xs text-slate-600">{safePage} / {totalPages}</span>
                    <button
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={safePage === totalPages || isAll}
                        className="px-2 py-1 text-sm rounded border border-slate-300 disabled:opacity-40"
                        title="Siguiente"
                    >
                        ›
                    </button>
                    <button
                        onClick={() => setPage(totalPages)}
                        disabled={safePage === totalPages || isAll}
                        className="px-2 py-1 text-sm rounded border border-slate-300 disabled:opacity-40"
                        title="Última"
                    >
                        »
                    </button>
                </div>
            </div>
        </div>
    );

    /* =================== RENDER =================== */
    if (!isAdmin) {
        return (
            <div className="space-y-3">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gradient-to-r from-slate-100 to-slate-200 rounded-lg border text-xs">
                    <div className="col-span-7 font-bold uppercase text-slate-600 tracking-wider">Comentario</div>
                    <div className="col-span-2 font-bold uppercase text-slate-600 tracking-wider">Tipo</div>
                    <div className="col-span-3 font-bold uppercase text-slate-600 tracking-wider text-right">Monto</div>
                </div>

                {ControlsBar}

                <div className="space-y-1.5">
                    {paged.map((r) => {
                        const isAbono = r.naturaleza.includes("(+)") || r.naturaleza.toLowerCase().includes("abono");
                        return (
                            <div
                                key={r.id}
                                className="grid grid-cols-12 gap-2 items-center rounded-lg border border-slate-200 bg-white hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 p-3 transition-all duration-200 hover:shadow-sm"
                            >
                                <div className="col-span-7 min-w-0">
                                    <div className="text-slate-700 text-sm truncate" title={r.comentario || "Sin comentario"}>
                                        {r.comentario || <span className="text-slate-400 italic">Sin comentario</span>}
                                    </div>
                                    <div className="text-xs text-slate-400 mt-0.5">{fdate(r.fecha_registro_pago)}</div>
                                </div>

                                <div className="col-span-2">
                                    <span
                                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border border-blue-200 bg-blue-50 text-blue-700"
                                        title={r.tipo_raw}
                                    >
                                        {r.tipo_label}
                                    </span>
                                </div>

                                <div className="col-span-3 text-right">
                                    <div className={`font-bold text-base ${isAbono ? "text-green-600" : "text-red-600"}`}>
                                        {isAbono ? "+" : "-"}{money(Math.abs(r.monto))}
                                    </div>
                                    <div className="text-xs text-slate-500">{isAbono ? "Abono" : "Cargo"}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-4 p-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-lg border">
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                        <span className="text-slate-600">
                            Total filtrado: <span className="font-medium">{filtered.length}</span> movimientos
                        </span>
                        <span className="text-slate-600">
                            Balance filtrado:
                            <span className={`font-bold ml-1 ${totFiltrado.balance >= 0 ? "text-green-600" : "text-red-600"}`}>
                                {money(totFiltrado.balance)}
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    // ===== Admin =====
    return (
        <div className="overflow-x-auto bg-white rounded-lg shadow-sm border border-slate-200">
            {ControlsBar}

            <table className="min-w-full text-sm">
                <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200">
                    <tr className="text-left">
                        <th className="py-3 px-4 font-semibold text-slate-700 uppercase tracking-wide text-xs">Fecha de aplicación</th>
                        <th className="py-3 px-4 font-semibold text-slate-700 uppercase tracking-wide text-xs">Tipo</th>
                        <th className="py-3 px-4 font-semibold text-slate-700 uppercase tracking-wide text-xs text-center">Naturaleza</th>
                        <th className="py-3 px-4 font-semibold text-slate-700 uppercase tracking-wide text-xs text-right">Monto</th>
                        <th className="py-3 px-4 font-semibold text-slate-700 uppercase tracking-wide text-xs">Comentario</th>
                        <th className="py-3 px-4 font-semibold text-slate-700 uppercase tracking-wide text-xs">Comentario interno</th>
                        <th className="py-3 px-4 font-semibold text-slate-700 uppercase tracking-wide text-xs">Creado</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {paged.map((r, index) => {
                        const isAbono = r.naturaleza.includes("(+)");
                        return (
                            <tr
                                key={r.id}
                                className={`transition-colors duration-200 hover:bg-slate-50/80 ${index % 2 === 0 ? "bg-white" : "bg-slate-25"}`}
                            >
                                <td className="py-3 px-4 text-slate-600 font-medium">{fdate(r.fecha_registro_pago)}</td>
                                <td className="py-3 px-4 text-slate-800 font-medium">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-blue-400 rounded-full" />
                                        <span className="truncate max-w-[220px]" title={r.tipo_raw}>{r.tipo_label}</span>
                                    </div>
                                </td>
                                <td className="py-3 px-4 text-center">
                                    <span
                                        className={`inline-flex items-center px-2.5 py-1.5 rounded-full text-xs font-medium ${isAbono
                                            ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                                            : "bg-rose-100 text-rose-800 border border-rose-200"
                                            }`}
                                    >
                                        {isAbono ? "+" : "−"}{r.naturaleza.replace(/[()+-]/g, "").trim()}
                                    </span>
                                </td>
                                <td className="py-3 px-4 text-right">
                                    <div className={`font-bold text-base ${isAbono ? "text-emerald-700" : "text-rose-700"}`}>
                                        {money(r.monto)}
                                    </div>
                                </td>
                                <td className="py-3 px-4 text-slate-700">
                                    <div className="max-w-[280px]">
                                        {r.comentario ? (
                                            <span className="truncate block" title={r.comentario}>{r.comentario}</span>
                                        ) : (
                                            <span className="text-slate-400 italic text-xs">Sin comentario</span>
                                        )}
                                    </div>
                                </td>
                                <td className="py-3 px-4 text-slate-700">
                                    <div className="max-w-[280px]">
                                        {r.comentario_interno ? (
                                            <span className="truncate block" title={r.comentario_interno}>{r.comentario_interno}</span>
                                        ) : (
                                            <span className="text-slate-400 italic text-xs">Sin comentario interno</span>
                                        )}
                                    </div>
                                </td>
                                <td className="py-3 px-4 text-slate-500 text-xs">{fdate(r.fecha_alta_registro)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}